import BaseController from "./base_controller"

export default class extends BaseController {
  static targets = ["input", "results"]
  static outlets = ["vessel-group", "calculator"]
  static values = {
    isValueGuidance: Boolean,
    isIntrinsicValue: Boolean,
    isDesign: Boolean,
    isAdminVessel: Boolean,
  }

  connect() {
    this.handleKeyup = this.delay(this.search.bind(this), 250)
    if (!this.isAdminVesselValue)
      this.bindOutsideClick()
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick)
  }

  onKeyup(event) {
    const code = event.keyCode || event.which
    // Ignore arrow keys
    if ([13, 37, 38, 39, 40].includes(code)) return

    this.handleKeyup(event.target)
  }

  search(inputEl) {
    const searchTerm = inputEl.value

    let type = null
    if (!this.isAdminVesselValue) {
      const path = window.location.pathname
      type = path.substring(path.lastIndexOf("/") + 1)
    }

    if (searchTerm === "") {
      this.resultsTarget.innerHTML = ''
      return
    }

    let url
    if (this.isAdminVesselValue) {
      url = this.adminVesselsSearchUrl(searchTerm, type)
    } else if (this.isDesignValue) {
      url = this.calculatorDesignSearchUrl(searchTerm, type)
    } else {
      url = this.calculatorVesselsSearchUrl(searchTerm, type)
    }
    if (this.isValueGuidanceValue)
      url += this.valueGuidanceParam()
    if (this.isIntrinsicValueValue)
      url += this.intrinsicValueParam()

    fetch(url, {
      headers: { "Accept": "text/html" },
      credentials: "same-origin",
    })
        .then(response => response.text())
        .then(html => this.resultsTarget.innerHTML = html)
  }

  bindOutsideClick() {
    this.handleOutsideClick = this.hideSearchOnOutsideClick.bind(this)
    document.addEventListener("click", this.handleOutsideClick)
  }

  hideSearchOnOutsideClick(event) {
    if (!this.inputTarget.contains(event.target) && !this.resultsTarget.contains(event.target)) {
      this.element.remove()
      const $calculator = document.getElementById("calculator-mobile")
      if ($calculator) {
        this.dispatch('showVessels', { target: $calculator })
      }
    }
  }

  selectResult(event) {
    const results = this.resultsTarget.querySelectorAll(".vessel-search-result")
    results.forEach(r => r.classList.remove("selected"))
    event.currentTarget.classList.add("selected")
  }

  submitAdminVessel(event) {
    event.preventDefault()
    event.currentTarget.querySelector("a")?.click()
  }

  submitCalculatorVessel(event) {
    event.preventDefault()
    const li = event.currentTarget
    if (li.classList.contains("disabled")) {
      if (li.dataset.vesselSearchMessage) this.notifyAlreadyAdded(event)
      return
    }

    if (!this.isDesignValue && !this.isAdminVesselValue) {
      const vesselType = li.dataset.vesselType
      const path = window.location.pathname
      const calculatorType = path.substring(path.lastIndexOf("/") + 1)

      if (vesselType && calculatorType && vesselType !== calculatorType) {
        this.#showVesselTypeMismatchWarning(li, calculatorType)
        return
      }
    }

    this.#performSubmit(li)
  }

  #showVesselTypeMismatchWarning(li, calculatorType) {
    const label = calculatorType.charAt(0).toUpperCase() + calculatorType.slice(1)
    window.dispatchEvent(new CustomEvent('show-confirmation', {
      detail: {
        title: 'Vessel type mismatch',
        message: `This vessel is not a ${label}. Are you sure you want to proceed?`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
        onConfirm: () => this.#performSubmit(li),
      }
    }))
  }

  #performSubmit(li) {
    const url = li.dataset.url
    const imo = li.dataset.imo
    const name = li.dataset.name

    const formData = new FormData()
    formData.append("vessel[imo]", imo)
    formData.append("vessel[name]", name)

    const forwardParam = new URLSearchParams(window.location.search).get('forward')
    if (forwardParam) formData.append("forward", forwardParam)

    const assumptionDate = document.querySelector(".future-assumptions-datepicker")?.value
    if (assumptionDate) {
      formData.append("assumption_date", assumptionDate)
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
    }

    return fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/javascript",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
      body: formData
    })
        .then(response => response.text())
        .then(js => {
          eval(js) // ⚠️ still mimics Rails UJS for JS response, temporary compromise!
        })
  }

  handleKeydown(event) {
    const results = Array.from(this.resultsTarget.querySelectorAll(".vessel-search-result"))
    const selected = this.resultsTarget.querySelector(".selected")

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveSelection(results, selected, 1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveSelection(results, selected, -1)
        break
      case "Enter":
        event.preventDefault()
        if (!selected) return

        if (this.isAdminVesselValue)
          this.submitAdminVessel({
            currentTarget: selected, preventDefault: () => {
            }
          })
        else
          this.submitCalculatorVessel({
            currentTarget: selected, preventDefault: () => {
            }
          })
        break
    }
  }

  moveSelection(results, currentSelected, direction) {
    let newIndex = 0

    if (currentSelected) {
      const currentIndex = results.indexOf(currentSelected)
      newIndex = (currentIndex + direction + results.length) % results.length
      currentSelected.classList.remove("selected")
    }
    results[newIndex]?.classList.add("selected")
  }

  notifyAlreadyAdded(event) {
    const el = event.currentTarget
    let message = el.dataset.vesselSearchMessage

    if (this.hasVesselGroupOutlet && !this.element.closest('#calculator-mobile')) {
      // Both mobile and desktop calculators match the outlet selector; only desktop vessels live inside a .vessel-form
      const vesselForm = this.calculatorOutlets.map(c => c.getVesselFormByImo(el.dataset.imo)).find(Boolean)
      const vesselGroupId = vesselForm?.querySelector('[name="vessel[group_id]"]')?.value || ''
      const activeGroupId = this.vesselGroupOutlet.activeGroupId

      if (vesselGroupId !== activeGroupId) {
        const groupName = this.vesselGroupOutlet.groupNameForId(vesselGroupId)

        if (groupName) {
          message += ' ' + el.dataset.vesselSearchGroupTemplate.replace('__GROUP__', groupName)
        }
      }
    }

    this.dispatchNotify(message)
  }

  existingVesselImos() {
    const vesselsElements = document.querySelectorAll(".vessel-form");
    return Array.from(vesselsElements)
        .map(el => el.dataset.imo)
        .filter(imo => imo !== undefined)
  }

  adminVesselsSearchUrl(searchTerm, type) {
    return "/admin-vessels?search_term=" +
        encodeURIComponent(searchTerm) +
        "&imos[]=" +
        this.existingVesselImos().map(encodeURIComponent).join("&imos[]=") +
        "&admin_search=true" +
        (type !== null ? "&type=" + encodeURIComponent(type) : "")
  }

  calculatorVesselsSearchUrl(searchTerm, type) {
    return "/vessels?search_term=" +
        encodeURIComponent(searchTerm) +
        "&imos[]=" +
        this.existingVesselImos().map(encodeURIComponent).join("&imos[]=") +
        (type !== null ? "&type=" + encodeURIComponent(type) : "")
  }

  calculatorDesignSearchUrl(searchTerm, type) {
    return "/vessels?search_term=" +
        encodeURIComponent(searchTerm) +
        "&imos[]=" +
        this.existingVesselImos().map(encodeURIComponent).join("&imos[]=") +
        (type !== null ? "&type=" + encodeURIComponent(type) : "") +
        "&design=true"
  }

  valueGuidanceParam() {
    return "&value_guidance=true"
  }

  intrinsicValueParam() {
    return "&intrinsic_value=true"
  }
}
